import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ModalService } from '../../../shared/design-system/organisms/p-modal/p-modal.service';
import { Nullable } from '../../../shared/types';
import { Utilisateur } from '../../models/utilisateur';
import { UtilisateurService } from '../../services/utilisateur.service';

enum ProfilModalId {
  OTP_MODAL = 'OTP_MODAL',
}

// TODO : il faut faire une deuxième refacto pour séparer les appels API et les appels de validation (Mon profil, Profil Collaborateur)
// L'idée c'est de suivre le principe des Smart et Dumb components, ou ce composant ProfileFormComponent est un Dumb component qui va emettre des événements
// et les Smart components (Mon profil, Profil Collaborateur) vont écouter ces événements et faire les appels API nécessaires
@Component({
  selector: 'app-profile-form',
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss'],
})
export class ProfileFormComponent implements OnChanges, OnInit, OnDestroy {
  @Input() userToEdit!: Partial<Nullable<Utilisateur>>;
  @Input() canEdit!: Utilisateur.CanEdit;
  @Input() displayEditButton!: boolean;
  @Input() isCurrentUserIdNot = false;

  @Output() loadUser = new EventEmitter<void>();

  errorMsg!: string;
  successMsg!: string;
  errorValidationCodeMsg!: string;
  isEditLoading = false;
  isValidationCodeLoading = false;
  isModalOTPOpen = false;
  newMail!: string | undefined;
  profilModalId = ProfilModalId;

  private readonly destroy$ = new Subject<void>();

  profileFormGroup = new FormGroup({
    login: new FormControl('', [Validators.required]),
    nom: new FormControl('', [Validators.required]),
    prenom: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.email, Validators.required]),
    phone: new FormControl(''),
  });

  otpForm = new FormGroup({
    otp: new FormControl('', [Validators.required]),
  });

  get isBasicInfosDirty(): boolean {
    return (
      this.userToEdit?.nom !== this.profileFormGroup.value.nom ||
      this.userToEdit?.prenom !== this.profileFormGroup.value.prenom ||
      this.userToEdit?.phone !== this.profileFormGroup.value.phone
    );
  }

  get isEmailDirty(): boolean {
    return this.userToEdit?.email !== this.profileFormGroup.value.email;
  }

  get isOtpDirty(): boolean {
    return this.otpForm.value.otp !== '';
  }

  constructor(
    private readonly utilisateurService: UtilisateurService,
    private readonly modalService: ModalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userToEdit']?.currentValue) {
      this.profileFormGroup.patchValue(changes['userToEdit'].currentValue);
    }
  }

  ngOnInit(): void {
    this.modalService
      .getModalState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((modalId) => {
        this.isModalOTPOpen = modalId === ProfilModalId.OTP_MODAL;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async saveProfile(): Promise<void> {
    try {
      this.resetMessages();
      this.isEditLoading = true;

      if (this.isEmailDirty) {
        await this.updateEmail();
        // Pour des raison backend, on est obliger de mettre le mail avec une valeur valide
        // TODO : conception à revoir pour éviter cette contrainte
        if (this.userToEdit && !this.userToEdit?.email) {
          this.userToEdit.email = this.profileFormGroup.controls.email
            .value as string;
        }
      }

      if (this.isBasicInfosDirty) {
        await this.updateBasicInfos();
      }

      this.isEditLoading = false;
      this.loadUser.emit();
      // TODO : gérer les erreurs correctement dans NPER-812
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.errorMsg = error.message;
      this.isEditLoading = false;
    }
  }

  async validerCodeVerification(): Promise<void> {
    try {
      this.resetMessages();
      this.isValidationCodeLoading = true;

      await this.utilisateurService.validateEmail(
        this.otpForm.controls.otp.value as string,
        this.userToEdit as Utilisateur,
        this.newMail as string,
      );

      this.modalService.hideModal();

      this.successMsg =
        'Votre adresse email a été correctement mise à jour et validée';

      this.otpForm.reset();
      this.newMail = undefined;
      this.isValidationCodeLoading = false;

      this.loadUser.emit();
      // TODO : gérer les erreurs correctement dans NPER-812
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.isValidationCodeLoading = false;
      this.errorValidationCodeMsg = error.message;
    }
  }

  async onCloseOtpModal(): Promise<void> {
    this.otpForm.reset();
    this.profileFormGroup.controls.email.setValue(
      this.userToEdit?.email as string,
    );
    this.resetMessages();
    this.errorValidationCodeMsg = '';
    this.newMail = '';
  }

  private async updateBasicInfos(): Promise<void> {
    const { nom, prenom, phone } = this.profileFormGroup.controls;
    await this.utilisateurService.updateBasicInfos({
      ...(this.userToEdit as Utilisateur),
      nom: nom.value as string,
      prenom: prenom.value as string,
      phone: phone.value as string,
    });

    this.successMsg = 'Vos informations ont bien été mises à jour.';
  }

  private async updateEmail(): Promise<void> {
    const { email } = this.profileFormGroup.controls;
    if (this.isCurrentUserIdNot) {
      await this.updateEmailIdNot(email.value as string);
    } else {
      await this.updateEmailXNot(email.value as string);
    }
  }

  private async updateEmailIdNot(email: string): Promise<void> {
    await this.utilisateurService.updateEmailIdNot(
      email,
      this.userToEdit as Utilisateur,
    );

    this.successMsg =
      'Votre adresse email a été correctement mise à jour et validée';
  }

  private async updateEmailXNot(email: string): Promise<void> {
    this.newMail = await this.utilisateurService.updateEmail(email);
    this.modalService.showModal(ProfilModalId.OTP_MODAL);
  }

  private resetMessages(): void {
    this.errorMsg = '';
    this.successMsg = '';
  }
}
